import { FunctionComponent, useCallback } from "react";
import { useTranslation } from "react-i18next";

import { Dropdown } from "@/components/Dropdown/Dropdown";
import {
	SUBTITLE_POSITIONS,
	SUBTITLE_SIZES,
	SUBTITLE_STYLES,
	SubtitlePosition,
	SubtitleSize,
	SubtitleStyle,
} from "@/pages/Private/redux/media/videoWizard.schema";
import { classNames } from "@/utils/classNames";

interface SubtitlePickerProps {
	style: SubtitleStyle;
	position: SubtitlePosition;
	size: SubtitleSize;
	disabled?: boolean;
	onChange: (patch: {
		subtitleStyle?: SubtitleStyle;
		subtitlePosition?: SubtitlePosition;
		subtitleSize?: SubtitleSize;
	}) => void;
}

/**
 * Each tile shows what the setting *looks* like rather than only naming it — the mockup's
 * `sub-tile` samples. "Klassisch (Balken)" and "Modern (Wort-Highlight)" are indisting-
 * uishable as words and obvious as pictures, and the author is choosing a picture.
 */
const StyleSample: FunctionComponent<{ style: SubtitleStyle; noneLabel: string }> = ({
	style,
	noneLabel,
}) => {
	const sample = "Es ist Nacht, als der Alarm eingeht.";

	if (style === SubtitleStyle.NONE) {
		return (
			<span className="flex h-12 items-center justify-center rounded bg-ink/90 text-xs font-bold text-white/60">
				{noneLabel}
			</span>
		);
	}

	return (
		<span className="flex h-12 items-end justify-center rounded bg-ink/90 px-2 pb-1.5">
			{style === SubtitleStyle.CLASSIC ? (
				<span className="rounded bg-black/70 px-1.5 py-0.5 text-xs font-semibold text-white">
					{sample}
				</span>
			) : (
				<span className="text-xs font-semibold text-white">
					{/* The highlighted word inverts against the frame, which is what a karaoke
					    subtitle does and what keeps the sample readable at this size. */}
					Es ist <span className="rounded bg-accent-tint px-1 text-text">Nacht</span>, als der Alarm
					eingeht.
				</span>
			)}
		</span>
	);
};

export const SubtitlePicker: FunctionComponent<SubtitlePickerProps> = ({
	style,
	position,
	size,
	disabled,
	onChange,
}) => {
	const { t } = useTranslation();
	const ts = useCallback(
		(key: string, options?: Record<string, unknown>) => t(`videoWizard.${key}`, options ?? {}),
		[t]
	);

	const positionOptions = SUBTITLE_POSITIONS.map((value) => ({
		id: value,
		title: ts(`steps.audio.positions.${value}`),
	}));
	const sizeOptions = SUBTITLE_SIZES.map((value) => ({
		id: value,
		title: ts(`steps.audio.sizes.${value}`),
	}));

	return (
		<section className="space-y-3 rounded-card border border-border-soft bg-white p-4">
			<span className="block text-sm font-bold text-ink">{ts("steps.audio.subtitleLabel")}</span>

			<div className="grid gap-2 sm:grid-cols-3">
				{SUBTITLE_STYLES.map((value) => (
					<button
						key={value}
						className={classNames(
							"space-y-2 rounded-card border p-2.5 text-left transition-colors",
							value === style
								? "border-accent bg-accent-tint"
								: "border-border bg-white hover:border-accent"
						)}
						disabled={disabled}
						type="button"
						onClick={() => onChange({ subtitleStyle: value })}
					>
						<StyleSample noneLabel={ts("steps.audio.subtitleNoneSample")} style={value} />
						<span className="block text-xs font-bold text-ink">
							{ts(`steps.audio.subtitleStyles.${value}`)}
						</span>
					</button>
				))}
			</div>

			{/* Position and size describe where the burnt-in text sits; without any subtitles
			    there is nothing for them to describe. */}
			<div className="flex flex-wrap gap-3">
				<div className="min-w-[180px] flex-1">
					<Dropdown
						data={positionOptions}
						disabled={disabled || style === SubtitleStyle.NONE}
						handleSelect={(item) =>
							item.id !== position && onChange({ subtitlePosition: item.id as SubtitlePosition })
						}
						label={ts("steps.audio.positionLabel")}
						value={positionOptions.find((option) => option.id === position)}
					/>
				</div>
				<div className="min-w-[180px] flex-1">
					<Dropdown
						data={sizeOptions}
						disabled={disabled || style === SubtitleStyle.NONE}
						handleSelect={(item) =>
							item.id !== size && onChange({ subtitleSize: item.id as SubtitleSize })
						}
						label={ts("steps.audio.sizeLabel")}
						value={sizeOptions.find((option) => option.id === size)}
					/>
				</div>
			</div>
		</section>
	);
};
